import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface EmptyStateProps {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** One primary action (verb): "Create invoice". */
  action?: ReactNode;
  secondaryAction?: ReactNode;
  /** Optional lean2automate photo (e.g. "/brand/photos/finance-desk.jpg"), shown beside the text on wide screens. */
  photo?: string;
  /** Floating chip over the photo (site `.float-chip`), e.g. "₹2.4 L collected this week". */
  photoChip?: ReactNode;
  eyebrow?: ReactNode;
  size?: "sm" | "md" | "lg";
  /** `card` wraps it in a bordered surface; `plain` sits on the page. */
  variant?: "card" | "plain";
  className?: string;
  children?: ReactNode;
}

/**
 * Consistent empty / zero / "being built" states. Explain the value, then offer one action
 * (brand voice §8): "Nothing overdue. When an invoice passes its due date it shows up here…"
 */
export function EmptyState({ icon, title, description, action, secondaryAction, photo, photoChip, eyebrow, size = "md", variant = "plain", className, children }: EmptyStateProps) {
  const text = (
    <div className={cn("flex flex-col", photo ? "items-start text-left" : "items-center text-center")}>
      {icon && (
        <div className="relative mb-5">
          <div aria-hidden className="absolute -inset-3 rounded-[22px] bg-[radial-gradient(closest-side,var(--ui-primary-soft),transparent)] opacity-80" />
          <div
            className={cn(
              "border-brand-gradient relative flex items-center justify-center rounded-[16px] text-primary-ink shadow-sm",
              size === "sm" ? "size-11 [&_svg]:size-5" : "size-14 [&_svg]:size-6",
            )}
          >
            {icon}
          </div>
        </div>
      )}
      {eyebrow && <span className="eyebrow mb-3">{eyebrow}</span>}
      <h2 className={cn("font-display font-semibold tracking-tight text-fg", size === "sm" ? "text-md" : size === "lg" ? "text-2xl" : "text-xl")}>{title}</h2>
      {description && <p className={cn("mt-2 max-w-md text-fg-muted", size === "sm" ? "text-sm" : "text-base leading-relaxed")}>{description}</p>}
      {children}
      {(action || secondaryAction) && (
        <div className={cn("mt-5 flex flex-wrap items-center gap-2", !photo && "justify-center")}>
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={cn(
        "w-full",
        variant === "card" && "rounded-card border border-line bg-surface shadow-sm",
        size === "sm" ? "px-4 py-8" : size === "lg" ? "px-6 py-16" : "px-6 py-12",
        className,
      )}
    >
      {photo ? (
        <div className="mx-auto grid max-w-4xl items-center gap-8 md:grid-cols-[1.05fr_0.95fr]">
          {text}
          <div className="relative hidden aspect-[16/11] overflow-hidden rounded-[22px] bg-surface-2 shadow-lg md:block">
            <img src={photo} alt="" loading="lazy" className="size-full object-cover" />
            <div aria-hidden className="photo-scrim absolute inset-0" />
            {photoChip && (
              <div className="absolute bottom-4 left-4 inline-flex max-w-[calc(100%-2rem)] items-center gap-2 rounded-pill bg-white/95 px-3.5 py-2 text-sm font-extrabold text-navy shadow-md backdrop-blur-sm">
                <span aria-hidden className="size-2 rounded-full bg-teal" />
                {photoChip}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="mx-auto max-w-lg">{text}</div>
      )}
    </div>
  );
}
